import { Box } from '@mui/material';
import { useDialog } from 'components/core/Dialog/common/DialogContext';
import { ReactElement, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { showErrorToastAndLog } from 'services/toaster';
import { useCurrentUserInProjectModel } from 'store/user';
import { getAccessLevelOnDropdownView } from 'shared/domain/issue/mapping/accessLevel/toOnView/getAccessLevelOnDropdownView';
import { UserOnViewWithIssueAccessLevel } from 'shared/domain/user/types/view';
import type { UserAccess, UserAccessRecord } from 'shared/domain/issue/mapping/accessLevel/toOnView/types';
import { AccessLevelModel } from 'shared/domain/issue/mapping/accessLevel/model/types';
import { ManagementSelect } from 'views/issue/issueView/AccessManagement/select';
import { useManagement } from 'views/issue/issueView/AccessManagement/useManagement';
import { getConfirmationDialogParams } from './getConfirmationDialogParams';
import { getAccessLevelIcon } from 'views/issue/issueView/AccessManagement/getIcon';
import { logError } from 'setup/logger/logError';
import { userOnViewWithIssueAccessLevelToTargetUserOnAccessChangeView } from 'shared/domain/user/mapping/toView';

export function AccessLevelSelect({
  userAccesses,
  user,
  issueId,
  siteId,
  processId,
}: {
  userAccesses: UserAccess[];
  user: UserOnViewWithIssueAccessLevel;
  issueId: string;
  siteId: string;
  processId: string;
}): ReactElement {
  const intl = useIntl();
  const { submitAccessChange, onAccessChange } = useManagement();
  const createDialog = useDialog();

  const submitter = useCurrentUserInProjectModel();

  const { disabled, disabledReasons, explanationLabel } =
    getAccessLevelOnDropdownView({
      issueUserAccesses: userAccesses,
      submitter,
      targetUser:
        userOnViewWithIssueAccessLevelToTargetUserOnAccessChangeView(user),
      siteId: siteId,
      processId: processId,
    });

  const handleAccessChange = async (
    newAccessLevel: AccessLevelModel,
    currentAccessLevel: AccessLevelModel,
    userId: string,
    userLabel: string
  ): Promise<void> => {
    if (newAccessLevel === currentAccessLevel) {
      return;
    }

    createDialog(
      getConfirmationDialogParams(
        intl,
        currentAccessLevel,
        newAccessLevel,
        userLabel
      )
    )
      .then(() => {
        onAccessChange(
          newAccessLevel,
          currentAccessLevel,
          userId,
          userLabel,
          userAccesses
        );

        submitAccessChange(issueId);
      })
      .catch((e) => {
        logError('Error while changing access level', e);
        showErrorToastAndLog('Error while changing access level',
          e
        );
      });
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
      <ManagementSelect
        userId={user._id}
        userLabel={user.label}
        userRole={user.role._id}
        currentAccess={user.accessLevel.value}
        onChange={handleAccessChange}
        disabled={disabled}
        disabledReasons={disabledReasons}
        Icon={getAccessLevelIcon(user.accessLevel.value, disabled)}
        explanationLabel={explanationLabel}
      />
    </Box>
  );
}
